import { createFileRoute, Link, useNavigate, Navigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ChevronLeft, ChevronRight,
  Check, CheckCircle2, Sparkles, Target, ExternalLink,
  HelpCircle, Save, Trophy, PartyPopper, Flag,
} from "lucide-react";
import {
  getMissionWorkspace,
  saveMissionStep,
  saveMissionAnswers,
  saveMissionDeliverable,
  toggleExternalCheck,
  finalizeMission,
  getBusinessWorkspace,
  getMissionCompletionSummary,
  getBlockMissionMap,
} from "@/lib/workspace.functions";
import { track } from "@/hooks/useAnalytics";
import { cmvBandFor } from "@/lib/deliverables";
import {
  BusinessPreviewPanel,
  BusinessPreviewDrawer,
  BlockSummary,
} from "@/components/members/BusinessPreviewPanel";
import {
  DecisionEngine,
  DecisionOverview,
  BusinessBlocksPanel,
  computeStructuringPct,
  projectedStructuringPct,
  estimateMinutes,
} from "@/components/members/DecisionEngine";
import {
  ENGINE_STATE_KEY,
  decisionsProgress,
  isDecisionComplete,
  normalizeMissionDecisions,
  resolveDecisionResumeState,
  type EngineState,
} from "@/lib/decision-engine";
import { BLOCK_BY_KEY, type BlockKey } from "@/lib/business-blocks";
import { CALCULATORS } from "@/lib/methodology/calculators";
import { deriveLiveBlockDraft, decisionImpactLabel, declaredFieldsForBlock } from "@/lib/decision-live";
import { IMPACTED_BY_SECTION, SECTION_BY_KEY } from "@/lib/business-plan/schema";
import { BuilderFeedbackPanel } from "@/components/business-builder/BuilderFeedbackPanel";
import { getBusinessPlanBundle } from "@/lib/business-plan/editor.functions";
import { BuilderContextStrip } from "@/components/business-builder/BuilderContextStrip";


type FinalizeResult = {
  nextMissionId: string | null;
  next: { id: string; title: string; stage_id: string } | null;
  currentStage: { id: string; title: string } | null;
  nextStage: { id: string; title: string } | null;
  stageChanged: boolean;
  journeyComplete: boolean;
};


export const Route = createFileRoute("/_authenticated/membros/meu-projeto/missao/$id")({
  head: () => ({
    meta: [
      { title: "Missão — Meu Projeto — Projeto 101" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>) => ({
    preview: s.preview === 1 || s.preview === "1" || s.preview === true ? 1 : undefined,
  }),
  component: MissionWorkspacePage,
});

type Step = "entender" | "decidir" | "construir" | "finalizar";
const STEPS: { key: Step; label: string; sub: string }[] = [
  { key: "entender", label: "Contexto", sub: "Entenda o que vamos construir" },
  { key: "decidir", label: "Decisões", sub: "Escolhas que definem seu negócio" },
  { key: "construir", label: "Plano", sub: "Sua configuração pronta" },
  { key: "finalizar", label: "Impacto", sub: "O que mudou no seu negócio" },
];

function MissionWorkspacePage() {
  const { id } = Route.useParams();
  const { preview } = Route.useSearch();
  const isPreview = preview === 1;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fetchWs = useServerFn(getMissionWorkspace);
  const saveStepFn = useServerFn(saveMissionStep);
  const saveAnswersFn = useServerFn(saveMissionAnswers);
  const saveDelivFn = useServerFn(saveMissionDeliverable);
  const toggleExtFn = useServerFn(toggleExternalCheck);
  const finalizeFn = useServerFn(finalizeMission);

  // In admin preview mode: never persist anything.
  const noop = async (..._args: any[]) => ({ ok: true } as any);
  const saveStep = isPreview ? (noop as typeof saveStepFn) : saveStepFn;
  const saveAnswers = isPreview ? (noop as typeof saveAnswersFn) : saveAnswersFn;
  const saveDeliv = isPreview ? (noop as typeof saveDelivFn) : saveDelivFn;
  const toggleExt = isPreview ? (noop as typeof toggleExtFn) : toggleExtFn;
  const finalize = isPreview ? (noop as typeof finalizeFn) : finalizeFn;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["mission-workspace", id, isPreview],
    queryFn: () => fetchWs({ data: { missionId: id, preview: isPreview } }),
  });

  // Sub-lote 3.C — live preview of Meu Negócio inside the workspace.
  // Admin preview mode still reads (RLS returns the admin's own project
  // or nothing); no writes happen from preview anyway.
  const fetchBusinessWs = useServerFn(getBusinessWorkspace);
  const { data: bizWs, isError: bizWsError } = useQuery({
    queryKey: ["business-workspace"],
    queryFn: () => fetchBusinessWs(),
    staleTime: 15_000,
  });

  // Mapa bloco → missão da jornada ativa; usado para tornar cada bloco
  // pendente do painel lateral clicável (vai direto para a missão que o
  // constrói).
  const fetchBlockMap = useServerFn(getBlockMissionMap);
  const { data: blockMap } = useQuery({
    queryKey: ["block-mission-map"],
    queryFn: () => fetchBlockMap(),
    staleTime: 60_000,
  });

  const state = data?.state as any;
  const initialStep: Step = (state?.current_step as Step) ?? "entender";
  const [step, setStep] = useState<Step>(initialStep);
  const [slideIdx, setSlideIdx] = useState<number>(state?.current_slide ?? 0);
  const [answers, setAnswers] = useState<Record<string, any>>(state?.answers ?? {});
  const [deliverable, setDeliverable] = useState<Record<string, any>>(state?.deliverable ?? {});
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  const [finalizeResult, setFinalizeResult] = useState<FinalizeResult | null>(null);

  // Após finalizar, buscar a "próxima construção" no servidor — mesmo quando
  // a página é recarregada (o finalizeResult em memória se perde). Isso evita
  // que o CTA caia para "Voltar ao Meu Projeto" quando ainda há missões
  // executáveis restantes.
  const fetchSummary = useServerFn(getMissionCompletionSummary);
  const finalizedAt = (data?.state as any)?.finalized_at ?? null;
  const { data: completionSummary } = useQuery({
    queryKey: ["mission-completion-summary", id, finalizedAt],
    queryFn: () => fetchSummary({ data: { missionId: id } }),
    enabled: !!finalizedAt && !isPreview,
    staleTime: 30_000,
  });

  // Sync when server state arrives (and fire mission_started / mission_resumed once).
  const telemetryFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!state) return;
    setStep((state.current_step as Step) ?? "entender");
    setSlideIdx(state.current_slide ?? 0);
    setAnswers(state.answers ?? {});
    setDeliverable(state.deliverable ?? {});
    if (!isPreview && telemetryFiredRef.current !== id) {
      telemetryFiredRef.current = id;
      const missionMeta = { metadata: { mission_id: id } };
      if ((data as any)?.resumed) track("mission_resumed", missionMeta);
      else if ((data as any)?.justStarted) track("mission_started", missionMeta);
    }
  }, [state?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced autosave for answers + deliverable
  const answersTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const delivTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Track first render per key so the initial state hydration doesn't trigger a save.
  const answersDirtyRef = useRef(false);
  const delivDirtyRef = useRef(false);
  useEffect(() => {
    if (!data) return;
    if (!answersDirtyRef.current) { answersDirtyRef.current = true; return; }
    clearTimeout(answersTimer.current);
    setSaveState("saving");
    answersTimer.current = setTimeout(async () => {
      try {
        await saveAnswers({ data: { missionId: id, answers } });
        setSavedAt(Date.now());
        setSaveState("saved");
        setSaveError(null);
      } catch (e: any) {
        setSaveState("error");
        setSaveError(e?.message ?? "Erro ao salvar.");
      }
    }, 600);
    return () => clearTimeout(answersTimer.current);
  }, [answers]); // eslint-disable-line
  useEffect(() => {
    if (!data) return;
    if (!delivDirtyRef.current) { delivDirtyRef.current = true; return; }
    clearTimeout(delivTimer.current);
    setSaveState("saving");
    delivTimer.current = setTimeout(async () => {
      try {
        await saveDeliv({ data: { missionId: id, deliverable } });
        setSavedAt(Date.now());
        setSaveState("saved");
        setSaveError(null);
      } catch (e: any) {
        setSaveState("error");
        setSaveError(e?.message ?? "Erro ao salvar.");
      }
    }, 600);
    return () => clearTimeout(delivTimer.current);
  }, [deliverable]); // eslint-disable-line

  const goStep = async (s: Step) => {
    setStep(s);
    await saveStep({ data: { missionId: id, currentStep: s } });
  };
  const goSlide = async (i: number) => {
    setSlideIdx(i);
    await saveStep({ data: { missionId: id, currentSlide: i } });
  };

  const finalizeMut = useMutation({
    mutationFn: () => finalize({ data: { missionId: id } }),
    onSuccess: (res: any) => {
      toast.success("Configuração salva no seu negócio.");
      // Broaden invalidations so every surface that shows progress refreshes.
      qc.invalidateQueries({ queryKey: ["active-project"] });
      qc.invalidateQueries({ queryKey: ["user-projects"] });
      qc.invalidateQueries({ queryKey: ["business-workspace"] });
      qc.invalidateQueries({ queryKey: ["mission-workspace", id] });
      qc.invalidateQueries({ queryKey: ["achievements"] });
      qc.invalidateQueries({ queryKey: ["weekly-summary"] });
      refetch();
      const fr: FinalizeResult = {
        nextMissionId: res?.nextMissionId ?? null,
        next: res?.next ?? null,
        currentStage: res?.currentStage ?? null,
        nextStage: res?.nextStage ?? null,
        stageChanged: !!res?.stageChanged,
        journeyComplete: !!res?.journeyComplete,
      };
      setFinalizeResult(fr);
      if (!isPreview) {
        track("mission_completed", { metadata: { mission_id: id } });
        if (fr.journeyComplete) track("journey_completed", { metadata: { mission_id: id } });
      }
      setStep("finalizar");
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao finalizar."),
  });

  // Hooks MUST run before any early return (React rules-of-hooks / error #310).
  // Use null-safe fallbacks so they stay stable when `data` is still loading.
  const _questions: any[] = (data as any)?.questions ?? [];
  const _deliverableSpec: any = (data as any)?.deliverable ?? null;
  const engineState: EngineState =
    (answers?.[ENGINE_STATE_KEY] as EngineState | undefined) ?? {};
  const decisionAnswers = useMemo(() => {
    const clone = { ...answers };
    delete clone[ENGINE_STATE_KEY];
    return clone;
  }, [answers]);
  const decisions = useMemo(
    () => normalizeMissionDecisions(_questions, decisionAnswers),
    [_questions, decisionAnswers],
  );
  const resume = useMemo(
    () => resolveDecisionResumeState({ decisions, answers: decisionAnswers, engineState }),
    [decisions, decisionAnswers, engineState],
  );
  const toastedSignaturesRef = useRef<Set<string>>(new Set());
  const liveDraft = useMemo(
    () => deriveLiveBlockDraft(_deliverableSpec, _questions, answers),
    [_deliverableSpec, _questions, answers],
  );

  if (isLoading) return <div className="h-96 animate-pulse rounded-2xl bg-black/5" />;
  if (!data) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-black/5 bg-white p-8 text-center">
        <p className="text-sm text-[var(--gray-ink)]">
          Nenhum projeto ativo. Escolha um negócio para começar.
        </p>
        <Link
          to="/membros/negocios"
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white"
        >
          Explorar negócios
        </Link>
      </div>
    );
  }

  const mission: any = data.mission;
  const stage: any = data.stage;
  const slides: any[] = data.slides;
  const questions: any[] = data.questions;
  const deliverableSpec: any = data.deliverable;
  const externals: any[] = data.externals;
  const resources: any[] = data.resources;
  const finalized = !!state?.finalized_at;
  const hasWorkspaceContent = slides.length > 0 || questions.length > 0 || deliverableSpec;

  // Legacy fallback: mission has none of the new content → show a simple
  // "prepare workspace" hint (still preserves legacy task-based experience
  // for admins to configure through /admin later).
  if (!hasWorkspaceContent) {
    return (
      <LegacyMissionFallback
        mission={mission}
        stage={stage}
        next={(finalizeResult as any)?.next ?? (completionSummary as any)?.next ?? (data as any)?.nextMission ?? null}
        journeyComplete={!!((finalizeResult as any)?.journeyComplete ?? (completionSummary as any)?.journeyComplete)}
        finalized={finalized}
        onAdvance={() => finalizeMut.mutate()}
        advancing={finalizeMut.isPending}
        isPreview={isPreview}
      />
    );
  }


  const currentSlide = slides[Math.min(slideIdx, Math.max(0, slides.length - 1))];
  const stepIndex = STEPS.findIndex((s) => s.key === step);

  // Sub-lote 3.C — which "Meu Negócio" block does this mission build?
  // Comes from mission_deliverables.target_field (whitelisted upstream).
  const currentBlockKey: BlockKey | null =
    (deliverableSpec?.target_field && BLOCK_BY_KEY[deliverableSpec.target_field])
      ? (deliverableSpec.target_field as BlockKey)
      : null;
  const currentBlock = currentBlockKey ? BLOCK_BY_KEY[currentBlockKey] : null;
  const workspaceObj = (bizWs?.workspace ?? null) as Record<string, unknown> | null;

  // ---- Decision Engine 2.1 ----
  // engineState / decisionAnswers / decisions / resume are computed above
  // the early returns so hook order stays stable.
  const updateEngineState = (patch: EngineState) => {
    setAnswers((prev) => ({
      ...prev,
      [ENGINE_STATE_KEY]: { ...((prev?.[ENGINE_STATE_KEY] as EngineState) ?? {}), ...patch },
    }));
  };
  // GS.4.1 — deduplicação inteligente de toasts.
  //
  // Assinatura estável: `${decisionId}|${normalized_answers_hash}|${band}`.
  // - Cenário A (repetir sem mudar): mesma assinatura → sem toast.
  // - Cenário B (mudar preço mas CMV segue na mesma faixa): assinatura muda
  //   apenas se algum ANSWER relevante mudou; sem toast quando a substância
  //   permanece igual (mesma resposta serializada).
  // - Cenário C (faixa do CMV muda): banda entra na assinatura para pricing
  //   → nova assinatura → novo toast.
  // - Cenário D (ativo antes parcial vira completo): resposta muda →
  //   assinatura muda → toast.
  // - Cenário E (hidratação, autosave, re-render sem "completed"): não
  //   entramos no branch, sem toast.
  // toastedSignaturesRef is declared above the early returns.
  const buildDecisionSignature = (
    d: import("@/lib/decision-engine").Decision | undefined,
    blockKey: BlockKey | null,
  ): string => {
    if (!d) return "";
    const keys = (d.questions ?? [])
      .map((q: any) => String(q?.key ?? ""))
      .filter(Boolean)
      .sort();
    const answersPart = keys.map((k) => [k, answers?.[k] ?? null]);
    let band = "";
    if (blockKey === "pricing") {
      // Faixa recalculada a partir do estado corrente (respostas em memória
      // + workspace), coerente com o que o painel lateral está mostrando.
      const p = (answers as any)?.pricing_price ?? (workspaceObj as any)?.pricing?.price;
      const c = (answers as any)?.pricing_cost ?? (workspaceObj as any)?.pricing?.cost;
      band = cmvBandFor(p, c);
    }
    // Serialização estável — mesma entrada → mesma string.
    return `${d.id}::${JSON.stringify(answersPart)}::${band}`;
  };
  const dispatchEngineEvent = (e: {
    type: string;
    decisionId?: string;
    questionKey?: string;
    phase?: string;
  }) => {
    if (e.type === "completed" && e.decisionId) {
      const d = decisions.find((x) => x.id === e.decisionId);
      const blockKey = (d?.block as BlockKey | undefined) ?? currentBlockKey ?? null;
      const signature = buildDecisionSignature(d, blockKey);
      if (signature && !toastedSignaturesRef.current.has(signature)) {
        toastedSignaturesRef.current.add(signature);
        const label = decisionImpactLabel({
          resultLabel: d?.resultLabel ?? null,
          blockKey,
          decisionTitle: d?.title ?? null,
        });
        toast.success(label, { description: "Seu Plano do Negócio foi atualizado." });
      }
    }
    if (isPreview) return;
    // Map engine events → analytics events (best-effort, safe if track is a no-op).
    try {
      const meta = { mission_id: id, decision_id: e.decisionId, question_key: e.questionKey, phase: e.phase };
      if (e.type === "context_viewed") track("decision_context_viewed" as any, { metadata: meta });
      else if (e.type === "answer_started") track("decision_answer_started" as any, { metadata: meta });
      else if (e.type === "resumed") track("decision_resumed" as any, { metadata: meta });
      else if (e.type === "completed") track("decision_completed" as any, { metadata: meta });
      else if (e.type === "impact_viewed") track("decision_impact_viewed" as any, { metadata: meta });
    } catch { /* telemetria não deve quebrar UI */ }
  };

  // liveDraft is computed above the early returns.

  const previewProps = {
    workspace: workspaceObj,
    currentBlockKey,
    // No passo "decidir" projetamos as respostas ao vivo. Nos demais passos,
    // o deliverable draft (StepConstruir) já é o rascunho canônico.
    currentDraft: step === "decidir" ? liveDraft : deliverable,
    saveState: isPreview ? ("idle" as const) : saveState,
    errorText: saveError,
    loadError: bizWsError,
    missionMap: (blockMap as any)?.map ?? null,
  };


  return (
    <div className="mx-auto max-w-6xl">
      {isPreview && (
        <div className="mb-4 rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-3 text-sm font-semibold text-[var(--navy)]">
          🛠️ Modo de visualização do administrador — nada será salvo, nenhum progresso será alterado.
        </div>
      )}
      <Link
        to="/membros/meu-projeto"
        className="mb-4 inline-flex items-center gap-1 text-sm text-[var(--gray-ink)] hover:text-[var(--navy)]"
      >
        <ArrowLeft size={14} /> Voltar
      </Link>

      {(data as any).business && (
        <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--green)]">
            Seu projeto
          </span>
          <h2 className="font-display text-xl font-extrabold text-[var(--navy)] lg:text-2xl">
            {(data as any).business.title}
          </h2>
          {(data as any).business.category?.name && (
            <span className="text-xs text-[var(--gray-ink)]">
              {(data as any).business.category.name}
            </span>
          )}
        </div>
      )}



      {/* Header */}
      <header className="mb-6 rounded-2xl border border-black/5 bg-white p-5 lg:p-6">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-[var(--navy)]/10 px-2.5 py-1 font-semibold text-[var(--navy)]">
            {stage?.title ?? "Construção"}
          </span>
          {(data as any)?.resumed && !finalized && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--gold)]/10 px-2.5 py-1 font-semibold text-[var(--gold)]">
              <Sparkles size={12} /> Continuando sua construção
            </span>
          )}
          {finalized && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--green)]/10 px-2.5 py-1 font-semibold text-[var(--green)]">
              <CheckCircle2 size={12} /> Concluída
            </span>
          )}
          {currentBlock && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--green)]/10 px-2.5 py-1 font-semibold text-[var(--green)]">
              <Target size={12} /> Construindo: {currentBlock.title}
            </span>
          )}
          <span className="ml-auto text-[11px] text-[var(--gray-ink)]/70">
            {saveState === "saving" && "Salvando…"}
            {saveState === "saved" && "Alterações salvas."}
            {saveState === "error" && (saveError ?? "Erro ao salvar.")}
          </span>

        </div>
        <h1 className="mt-2 font-display text-2xl font-extrabold text-[var(--navy)] lg:text-3xl">
          {mission.title}
        </h1>
        {mission.description && (
          <p className="mt-1 text-sm text-[var(--gray-ink)]">{mission.description}</p>
        )}

        {/* Step tabs */}
        <nav className="mt-5 grid grid-cols-4 gap-1.5">
          {STEPS.map((s, i) => {
            const active = s.key === step;
            const done = i < stepIndex || (i === stepIndex && finalized);
            return (
              <button
                key={s.key}
                onClick={() => goStep(s.key)}
                className={`rounded-xl px-3 py-2.5 text-left transition ${
                  active
                    ? "bg-[var(--navy)] text-white shadow-sm"
                    : done
                    ? "bg-[var(--green)]/10 text-[var(--green)] hover:bg-[var(--green)]/15"
                    : "bg-[var(--gray-soft)] text-[var(--gray-ink)] hover:bg-[var(--gray-soft)]/70"
                }`}
              >
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide opacity-80">
                  {done && <Check size={11} />} {i + 1}. {s.label}
                </div>
                <div className="mt-0.5 truncate text-xs font-medium">{s.sub}</div>
              </button>
            );
          })}
        </nav>
      </header>

      {/* UB.1 — mesma faixa consultiva usada no Structured Editor. */}
      {step !== "finalizar" && currentBlock && (
        <BuilderContextStrip
          whyNow={SECTION_BY_KEY[currentBlockKey as BlockKey]?.whyItMatters ?? "Peça essencial do seu plano."}
          buildsWhat={[currentBlock.title]}
          timeLabel={`${SECTION_BY_KEY[currentBlockKey as BlockKey]?.estimatedMinutes ?? 5} min · ${STEPS.findIndex((s) => s.key === step) + 1}/${STEPS.length} etapas`}
          impactedTitles={
            currentBlockKey
              ? (IMPACTED_BY_SECTION[currentBlockKey as BlockKey] ?? [])
                  .map((k) => SECTION_BY_KEY[k]?.title ?? k)
                  .filter(Boolean) as string[]
              : []
          }
          sectionKey={currentBlockKey ?? undefined}
          mode="guided"
        />
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">

        {/* Main area */}
        <section className="min-w-0 space-y-4">
          {step === "entender" && (
            <div className="space-y-4">
              <DecisionOverview
                decisions={decisions}
                currentBlockKey={currentBlockKey}
                workspace={workspaceObj}
              />
              <StepEntender
                slides={slides}
                index={Math.min(slideIdx, Math.max(0, slides.length - 1))}
                onGo={goSlide}
                onFinish={() => goStep("decidir")}
                resources={resources.filter(
                  (r) => r.anchor_type === "slide" && r.anchor_id === currentSlide?.id,
                )}
              />
            </div>
          )}
          {step === "decidir" && (
            <DecisionEngine
              decisions={decisions}
              answers={decisionAnswers}
              engineState={engineState}
              resume={resume}
              onChange={(k, v) => setAnswers((prev) => ({ ...prev, [k]: v }))}
              onEngineChange={updateEngineState}
              onEvent={dispatchEngineEvent}
              onBack={() => goStep("entender")}
              onNext={() => goStep("construir")}
              resources={resources}
              currentBlockKey={currentBlockKey}
              workspace={workspaceObj}
            />
          )}
          {step === "construir" && (
            <StepConstruir
              spec={deliverableSpec}
              answers={answers}
              questions={questions}
              deliverable={deliverable}
              setDeliverable={setDeliverable}
              onBack={() => goStep("decidir")}
              onConfirm={() => finalizeMut.mutate()}
              submitting={finalizeMut.isPending}
              externals={externals}
              externalChecks={(state?.external_checks as any) ?? {}}
              onToggleExt={async (key: string, done: boolean) => {
                await toggleExt({ data: { missionId: id, key, done } });
                refetch();
              }}
            />
          )}
          {step === "finalizar" && (
            <StepFinalizar
              currentBlockKey={currentBlockKey}
              workspace={workspaceObj}
              mission={mission}
              stage={stage}
              deliverable={deliverable}
              spec={deliverableSpec}
              answers={answers}
              questions={questions}
              finalized={finalized}
              result={finalizeResult ?? (completionSummary as any) ?? null}
              isPreview={isPreview}
              onEdit={() => goStep("construir")}
              onNext={() => {
                const effective = (finalizeResult ?? (completionSummary as any)) || {};
                const nextId = effective?.next?.id ?? effective?.nextMissionId ?? null;
                if (nextId) {
                  if (!isPreview) track("next_mission_opened", { metadata: { from_mission_id: id, to_mission_id: nextId } });
                  navigate({ to: "/membros/meu-projeto/missao/$id", params: { id: nextId }, search: {} });
                } else if (effective?.journeyComplete) {
                  navigate({ to: "/membros/meu-negocio" });
                } else {
                  navigate({ to: "/membros/meu-projeto" });
                }
              }}
            />
          )}
        </section>

        {/* Side panel */}
        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <BusinessBlocksPanel workspace={workspaceObj} currentBlockKey={currentBlockKey} />
          <BusinessPreviewPanel {...previewProps} />
          <SidePanel
            step={step}
            slidesTotal={slides.length}
            slideIdx={slideIdx}
            decisions={decisions}
            answers={decisionAnswers}
            deliverableSpec={deliverableSpec}
            deliverable={deliverable}
            finalized={finalized}
          />
        </aside>
      </div>

      {/* Mobile: floating access to the same preview */}
      <BusinessPreviewDrawer {...previewProps} />
    </div>
  );
}

// ---------- Step: Entender (slides) ----------
function StepEntender({
  slides, index, onGo, onFinish, resources,
}: {
  slides: any[];
  index: number;
  onGo: (i: number) => void;
  onFinish: () => void;
  resources: any[];
}) {
  if (!slides.length) {
    return (
      <div className="rounded-2xl border border-black/5 bg-white p-6 text-sm text-[var(--gray-ink)]">
        Este momento ainda não tem conteúdo. Avance para as decisões.
        <button
          onClick={onFinish}
          className="ml-2 rounded-md bg-[var(--navy)] px-3 py-1 text-xs font-semibold text-white"
        >
          Continuar
        </button>
      </div>
    );
  }
  const s = slides[index];
  const pct = Math.round(((index + 1) / slides.length) * 100);
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-6 lg:p-8">
      <div className="mb-3 flex items-center justify-between text-xs text-[var(--gray-ink)]">
        <span>{index + 1} de {slides.length}</span>
        <span className="font-semibold text-[var(--navy)]">{pct}%</span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-[var(--gray-soft)]">
        <div className="h-full rounded-full bg-[var(--green)] transition-[width]" style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-6 flex items-start gap-3">
        {s.icon && <div className="text-3xl">{s.icon}</div>}
        <h2 className="font-display text-2xl font-extrabold text-[var(--navy)] lg:text-3xl">{s.title}</h2>
      </div>
      {s.body && <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-[var(--gray-ink)]">{s.body}</p>}
      {s.example && (
        <div className="mt-4 rounded-lg border-l-4 border-[var(--gold)] bg-[var(--gold)]/[0.06] p-3 text-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gold)]">Exemplo</p>
          <p className="mt-1 text-[var(--navy)]">{s.example}</p>
        </div>
      )}
      {s.image_url && <img src={s.image_url} alt="" className="mt-5 w-full rounded-xl" />}

      {resources.length > 0 && <ContextualResources items={resources} />}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => onGo(Math.max(0, index - 1))}
          disabled={index === 0}
          className="inline-flex items-center gap-1 rounded-md border border-black/10 px-3 py-1.5 text-sm font-semibold text-[var(--gray-ink)] hover:bg-[var(--gray-soft)] disabled:opacity-40"
        >
          <ChevronLeft size={14} /> Voltar
        </button>
        <div className="flex gap-1">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-6 rounded-full transition ${
                i === index ? "bg-[var(--navy)]" : i < index ? "bg-[var(--green)]" : "bg-[var(--gray-soft)]"
              }`}
            />
          ))}
        </div>
        {index < slides.length - 1 ? (
          <button
            onClick={() => onGo(index + 1)}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--navy)] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[var(--navy)]/90"
          >
            Continuar <ChevronRight size={14} />
          </button>
        ) : (
          <button
            onClick={onFinish}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--green)] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[var(--green)]/90"
          >
            Começar a adaptar <ArrowRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// StepDecidir + QuestionField locais: removidos.
// A experiência de decidir agora é 100% delegada ao DecisionEngine
// (src/components/members/DecisionEngine.tsx) com estado retomável.


// ---------- Step: Construir ----------
function StepConstruir({
  spec, answers, questions, deliverable, setDeliverable,
  onBack, onConfirm, submitting, externals, externalChecks, onToggleExt,
}: any) {
  // Compose default fields from answers when deliverable is empty
  const composed = useMemo(() => {
    const base: Record<string, any> = {};
    (questions ?? []).forEach((q: any) => {
      const v = answers[q.key];
      if (v == null || v === "") return;
      if (Array.isArray(v)) {
        const opt = (q.options ?? []).filter((o: any) => v.includes(o.value)).map((o: any) => o.label);
        base[q.key] = opt.length ? opt.join(", ") : v.join(", ");
      } else if (typeof v === "object" && "label" in v) {
        base[q.key] = v.other ? `${v.label}: ${v.other}` : v.label;
      } else {
        base[q.key] = String(v);
      }
    });
    return { ...base, ...(deliverable ?? {}) };
  }, [answers, questions, deliverable]);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-black/5 bg-white p-6 lg:p-8">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
          Plano final
        </p>
        <h2 className="mt-1 font-display text-2xl font-extrabold text-[var(--navy)]">
          {spec?.title ?? "Sua configuração inicial"}
        </h2>
        {spec?.description && (
          <p className="mt-1 text-sm text-[var(--gray-ink)]">{spec.description}</p>
        )}

        <div className="mt-5 grid gap-3">
          {(questions ?? []).map((q: any) => {
            const v = composed[q.key];
            return (
              <div key={q.id} className="grid gap-1 rounded-lg border border-black/5 bg-[var(--gray-soft)]/40 p-3 sm:grid-cols-[180px_1fr]">
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                  {q.prompt.replace(/\?$/, "")}
                </span>
                <span className="text-sm text-[var(--navy)]">{v || <em className="text-[var(--gray-ink)]/60">—</em>}</span>
              </div>
            );
          })}
        </div>

        <CalculatorsBlock template={spec?.template} answers={answers} />

        <div className="mt-4">
          <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            Observações (opcional)
          </label>
          <textarea
            value={deliverable?.notes ?? ""}
            onChange={(e) => setDeliverable({ ...deliverable, notes: e.target.value })}
            rows={3}
            placeholder="Anote qualquer detalhe importante da sua configuração."
            className="mt-1.5 w-full rounded-md border border-black/10 p-2 text-sm outline-none focus:border-[var(--navy)]"
          />
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 rounded-md border border-black/10 px-3 py-1.5 text-sm font-semibold text-[var(--gray-ink)] hover:bg-[var(--gray-soft)]"
          >
            <ChevronLeft size={14} /> Ajustar decisões
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--green)]/90 disabled:opacity-50"
          >
            <Save size={14} /> {submitting ? "Salvando..." : "Confirmar e salvar no meu negócio"}
          </button>
        </div>
      </div>

      {externals?.length > 0 && (
        <div className="rounded-2xl border border-dashed border-[var(--navy)]/20 bg-white p-6">
          <div className="flex items-center gap-2">
            <Target size={16} className="text-[var(--navy)]" />
            <h3 className="font-display text-base font-extrabold text-[var(--navy)]">Ações externas</h3>
          </div>
          <p className="mt-1 text-xs text-[var(--gray-ink)]">
            Marque conforme executar fora da plataforma.
          </p>
          <ul className="mt-3 space-y-2">
            {externals.map((t: any) => {
              const done = !!externalChecks?.[t.key];
              return (
                <li key={t.id} className="flex items-start gap-3 rounded-lg border border-black/5 p-3">
                  <button
                    onClick={() => onToggleExt(t.key, !done)}
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                      done ? "border-[var(--green)] bg-[var(--green)] text-white" : "border-black/25 bg-white"
                    }`}
                  >
                    {done && <Check size={12} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-semibold ${done ? "text-[var(--gray-ink)] line-through" : "text-[var(--navy)]"}`}>
                      {t.title}
                    </p>
                    {t.description && <p className="mt-0.5 text-xs text-[var(--gray-ink)]">{t.description}</p>}
                    {t.url && (
                      <a href={t.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[var(--navy)] hover:underline">
                        Abrir <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// Renderiza calculadoras declaradas no template do entregável
// (mission_deliverables.template.calculators). Cada entrada mapeia
// `inputs` para chaves de `answers` (string) ou soma vários campos (array).
function CalculatorsBlock({ template, answers }: { template: any; answers: Record<string, any> }) {
  const calculators: Array<{ key: string; title?: string; inputs: Record<string, string | string[]> }> =
    Array.isArray(template?.calculators) ? template.calculators : [];
  if (calculators.length === 0) return null;

  const resolved = calculators.map((c) => {
    const fn = (CALCULATORS as Record<string, (i: any) => { metrics: any[]; warnings: string[] }>)[c.key];
    if (!fn) return null;
    const input: Record<string, any> = {};
    Object.entries(c.inputs || {}).forEach(([target, from]) => {
      if (Array.isArray(from)) {
        input[target] = from.reduce((sum, k) => sum + (Number(answers?.[k]) || 0), 0);
      } else {
        input[target] = answers?.[from];
      }
    });
    let result: { metrics: any[]; warnings: string[] } = { metrics: [], warnings: [] };
    try { result = fn(input); } catch { /* noop */ }
    return { key: c.key, title: c.title ?? c.key, ...result };
  }).filter(Boolean) as Array<{ key: string; title: string; metrics: any[]; warnings: string[] }>;

  if (resolved.length === 0) return null;

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-[var(--green)]" />
        <h3 className="font-display text-sm font-extrabold uppercase tracking-wide text-[var(--navy)]">
          Resultados calculados
        </h3>
      </div>
      {resolved.map((r) => (
        <div key={r.key} className="rounded-2xl border border-[var(--green)]/20 bg-[var(--green)]/5 p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--green)]">{r.title}</p>
          {r.metrics.length > 0 && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {r.metrics.map((m: any) => (
                <MetricCard key={m.key} metric={m} />
              ))}
            </div>
          )}
          {r.warnings.length > 0 && (
            <ul className="mt-3 space-y-1">
              {r.warnings.map((w: string, i: number) => (
                <li key={i} className="text-xs text-amber-700">⚠ {w}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

// GS.3 — Card de métrica com interpretação (banda + diagnóstico + consequência + recomendação).
function MetricCard({ metric: m }: { metric: any }) {
  const band = m.band as "ideal" | "attention" | "risk" | "neutral" | undefined;
  const bandStyle =
    band === "ideal"
      ? { chip: "bg-[var(--green)]/15 text-[var(--green)]", ring: "border-[var(--green)]/25" }
      : band === "attention"
        ? { chip: "bg-amber-100 text-amber-800", ring: "border-amber-200" }
        : band === "risk"
          ? { chip: "bg-red-100 text-red-800", ring: "border-red-200" }
          : { chip: "bg-[var(--gray-soft)] text-[var(--gray-ink)]", ring: "border-black/5" };

  const formatted =
    typeof m.value === "number"
      ? (m.unit === "R$"
          ? `R$ ${m.value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
          : m.unit
            ? `${m.value}${m.unit === "%" ? "%" : ` ${m.unit}`}`
            : String(m.value))
      : String(m.value);

  return (
    <div className={`rounded-xl border ${bandStyle.ring} bg-white p-3`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{m.label}</p>
        {m.band_label && (
          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${bandStyle.chip}`}>
            {m.band_label}
          </span>
        )}
      </div>
      <p className="mt-1 font-display text-xl font-extrabold text-[var(--navy)]">{formatted}</p>
      {m.hint && <p className="mt-1 text-[11px] text-[var(--gray-ink)]">{m.hint}</p>}
      {(m.diagnosis || m.consequence || m.recommendation) && (
        <dl className="mt-2 space-y-1.5 border-t border-black/5 pt-2 text-[11px] leading-snug">
          {m.diagnosis && (
            <div>
              <dt className="font-semibold text-[var(--gray-ink)]">Diagnóstico</dt>
              <dd className="text-[var(--navy)]">{m.diagnosis}</dd>
            </div>
          )}
          {m.consequence && (
            <div>
              <dt className="font-semibold text-[var(--gray-ink)]">Se ficar assim</dt>
              <dd className="text-[var(--navy)]">{m.consequence}</dd>
            </div>
          )}
          {m.recommendation && (
            <div>
              <dt className="font-semibold text-[var(--green)]">Recomendação</dt>
              <dd className="text-[var(--navy)]">{m.recommendation}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

// ---------- Step: Finalizar (UB.1 — unificado com BuilderFeedbackPanel) ----------
function StepFinalizar({
  mission, stage, deliverable, spec, answers, questions, finalized,
  result, isPreview, onEdit, onNext, currentBlockKey, workspace,
}: any) {
  const journeyComplete = !!result?.journeyComplete;
  const stageChanged = !!result?.stageChanged;
  const next = result?.next ?? null;
  const nextStageTitle = result?.nextStage?.title ?? null;

  // Derive "changedFields" from the mission answers (top 6 relevant).
  const changedFields = (questions ?? [])
    .map((q: any) => {
      const v = answers?.[q.key];
      if (v == null || v === "") return null;
      let label = "";
      if (Array.isArray(v)) {
        label = (q.options ?? []).filter((o: any) => v.includes(o.value)).map((o: any) => o.label).join(", ") || v.join(", ");
      } else if (typeof v === "object" && v && "label" in v) {
        label = (v as any).other ? `${(v as any).label}: ${(v as any).other}` : (v as any).label;
      } else {
        label = String(v);
      }
      return { key: q.key, label: q.prompt.replace(/\?$/, ""), value: label };
    })
    .filter(Boolean) as Array<{ key: string; label: string; value: string }>;

  // Impacted sections come from the schema dependency graph
  // (same source used by Structured), keyed by the block this mission builds.
  const impactedTitles: string[] = currentBlockKey
    ? (IMPACTED_BY_SECTION[currentBlockKey as BlockKey] ?? [])
        .map((k) => SECTION_BY_KEY[k]?.title ?? k)
        .filter(Boolean) as string[]
    : [];

  const sectionTitle =
    (currentBlockKey && SECTION_BY_KEY[currentBlockKey as BlockKey]?.title) ||
    spec?.title || mission?.title || "Esta parte";

  const continueLabel = journeyComplete
    ? "Ver meu negócio completo"
    : stageChanged
      ? `Começar próxima etapa${nextStageTitle ? ` — ${nextStageTitle}` : ""}`
      : next
        ? `Continuar para ${next.title}`
        : "Continuar construção";

  return (
    <div className="space-y-4">
      <BuilderFeedbackPanel
        sectionTitle={sectionTitle}
        result={{
          changedFields,
          unlockedTitles: [],
          impactedTitles,
          statusChange: { before: "in_progress", after: "complete" },
          nextStep: next
            ? {
                ctaLabel: next.title,
                description: stageChanged && nextStageTitle
                  ? `Próxima etapa: ${nextStageTitle}.`
                  : "Próxima construção do seu plano.",
                estimatedMinutes: 5,
                expectedImpact: "Continuar avançando no seu plano.",
              }
            : journeyComplete
              ? {
                  ctaLabel: "Seu negócio está estruturado",
                  description: "Cada decisão foi salva. Agora você tem uma configuração completa para colocar em prática.",
                  estimatedMinutes: 0,
                  expectedImpact: "Ver plano consolidado.",
                  finished: true,
                }
              : null,
        }}
        onContinue={onNext}
        onBack={onEdit}
        continueLabel={continueLabel}
        backLabel="Revisar minhas decisões"
        sectionKey={currentBlockKey ?? undefined}
        mode="guided"
      />

      {currentBlockKey && (
        <div className="mx-auto max-w-2xl">
          <BlockSummary
            blockKey={currentBlockKey}
            workspace={workspace}
            draft={deliverable}
          />
        </div>
      )}

      {deliverable?.notes && (
        <div className="mx-auto max-w-2xl rounded-lg border border-black/5 bg-white p-3 text-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Suas observações</p>
          <p className="mt-1 text-[var(--navy)]">{deliverable.notes}</p>
        </div>
      )}

      {!finalized && !isPreview && (
        <p className="mx-auto max-w-2xl text-xs text-[var(--gray-ink)]">
          Ainda não confirmado. Volte para <button onClick={onEdit} className="underline">Construir</button> e clique em "Confirmar".
        </p>
      )}
      {isPreview && (
        <p className="mx-auto max-w-2xl text-xs text-[var(--gray-ink)]">Modo de visualização — a navegação para a próxima missão fica disponível apenas para membros.</p>
      )}
    </div>
  );
}


// ---------- Side panel ----------
function SidePanel({
  step, slidesTotal, slideIdx, decisions, answers, deliverableSpec, deliverable, finalized,
}: {
  step: Step;
  slidesTotal: number;
  slideIdx: number;
  decisions: import("@/lib/decision-engine").Decision[];
  answers: Record<string, any>;
  deliverableSpec: any;
  deliverable: any;
  finalized: boolean;
}) {
  const { done: doneDecisions, total: totalDecisions, pct: decisionsPct } =
    decisionsProgress(decisions, answers);
  const pct = step === "finalizar" || finalized
    ? 100
    : step === "construir"
      ? 75
      : step === "decidir"
        ? Math.round(50 + decisionsPct * 0.25)
        : slidesTotal
          ? Math.round(((slideIdx + 1) / slidesTotal) * 40)
          : 0;

  return (
    <div className="rounded-2xl border border-black/5 bg-white p-5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
        Progresso desta construção
      </p>
      <div className="mt-2 flex items-baseline justify-between">
        <span className="font-display text-2xl font-extrabold text-[var(--navy)]">{pct}%</span>
        {totalDecisions > 0 && (
          <span className="text-[10px] font-semibold text-[var(--gray-ink)]">
            {doneDecisions}/{totalDecisions} decisões
          </span>
        )}
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--gray-soft)]">
        <div className="h-full bg-[var(--green)] transition-[width]" style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-5">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
          Decisões
        </p>
        {totalDecisions === 0 ? (
          <p className="text-xs text-[var(--gray-ink)]/70">Nenhuma decisão prevista.</p>
        ) : (
          <ul className="space-y-1.5 text-xs">
            {decisions.map((d) => {
              const complete = isDecisionComplete(d, answers);
              return (
                <li key={d.id} className="flex items-start gap-1.5">
                  <span className={`mt-0.5 h-3 w-3 shrink-0 rounded-full ${complete ? "bg-[var(--green)]" : "bg-[var(--gray-soft)]"}`} />
                  <span className={complete ? "text-[var(--navy)]" : "text-[var(--gray-ink)]/70"}>
                    {d.title}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {deliverableSpec && (
        <div className="mt-5 rounded-lg bg-[var(--gray-soft)]/50 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            Resultado desta construção
          </p>
          <p className="mt-1 text-sm font-semibold text-[var(--navy)]">{deliverableSpec.title}</p>
          {deliverable?.notes && (
            <p className="mt-2 text-xs text-[var(--gray-ink)] line-clamp-3">{deliverable.notes}</p>
          )}
        </div>
      )}

      <div className="mt-5 flex items-center gap-1.5 text-xs text-[var(--gray-ink)]">
        <HelpCircle size={12} /> Precisa de ajuda? Em breve.
      </div>
    </div>
  );
}

function ContextualResources({ items, compact }: { items: any[]; compact?: boolean }) {
  if (!items?.length) return null;
  return (
    <div className={`${compact ? "mt-2" : "mt-5"} rounded-lg border border-black/5 bg-[var(--beige)]/40 p-3`}>
      <p className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
        <Sparkles size={11} /> Material que ajuda aqui
      </p>
      <ul className="space-y-1">
        {items.map((r) => (
          <li key={r.id}>
            <a
              href={r.url ?? "#"}
              target={r.url ? "_blank" : undefined}
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--navy)] hover:underline"
            >
              {r.title} <ExternalLink size={10} />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LegacyMissionFallback({
  mission,
  stage,
  next,
  journeyComplete,
  finalized,
  onAdvance,
  advancing,
  isPreview,
}: {
  mission: any;
  stage: any;
  next: { id: string; title: string } | null;
  journeyComplete: boolean;
  finalized: boolean;
  onAdvance: () => void;
  advancing: boolean;
  isPreview: boolean;
}) {
  // UB.3 — Se essa missão está vinculada a uma seção do plano com editor
  // estruturado disponível, redireciona direto para o formulário de
  // preenchimento em vez de mostrar a tela "sem passo a passo".
  const fetchBundle = useServerFn(getBusinessPlanBundle);
  const { data: bundle, isLoading: bundleLoading } = useQuery({
    queryKey: ["business-plan-bundle"],
    queryFn: () => fetchBundle(),
    staleTime: 30_000,
  });
  const sections = bundle?.plan?.sections ?? [];
  const normalize = (s: string) => (s ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const missionTitleNorm = normalize(mission.title ?? "");
  let section =
    sections.find((s: any) => s.missionId === mission.id) ??
    sections.find((s: any) => {
      const t = normalize(s.section.title);
      return t && (missionTitleNorm.includes(t) || t.includes(missionTitleNorm));
    }) ??
    null;
  const resolution = section ? bundle?.editorsBySection?.[section.section.key] : null;
  if (!finalized && resolution && (resolution.mode === "structured" || resolution.mode === "dependency" || resolution.mode === "guided")) {
    const target =
      resolution.mode === "dependency" && resolution.dependencySectionKey
        ? resolution.dependencySectionKey
        : section!.section.key;
    return (
      <Navigate
        to="/membros/meu-negocio/secao/$section"
        params={{ section: target }}
        replace
      />
    );
  }
  if (bundleLoading) {
    return <div className="mx-auto h-40 max-w-2xl animate-pulse rounded-2xl bg-black/5" />;
  }

  const showNextCta = finalized && next && !journeyComplete;
  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/membros/meu-projeto" className="mb-3 inline-flex items-center gap-1 text-sm text-[var(--gray-ink)] hover:text-[var(--navy)]">
        <ArrowLeft size={14} /> Voltar
      </Link>
      <div className="rounded-2xl border border-dashed border-black/10 bg-white p-8 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
          {stage?.title}
        </p>
        <h1 className="mt-2 font-display text-2xl font-extrabold text-[var(--navy)]">{mission.title}</h1>
        {mission.description && <p className="mt-2 text-sm text-[var(--gray-ink)]">{mission.description}</p>}
        <p className="mt-6 text-sm text-[var(--gray-ink)]">
          Esta parte ainda não tem passo a passo guiado. Registre suas escolhas em{" "}
          <span className="font-semibold text-[var(--navy)]">Meu Negócio</span> e siga adiante quando quiser.
        </p>
        <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Link
            to="/membros/meu-negocio"
            className="inline-flex items-center gap-2 rounded-md border border-[var(--navy)] px-4 py-2 text-sm font-semibold text-[var(--navy)] hover:bg-[var(--navy)] hover:text-white"
          >
            Editar em Meu Negócio
          </Link>
          {!isPreview && !finalized && (
            <button
              type="button"
              onClick={onAdvance}
              disabled={advancing}
              className="inline-flex items-center gap-2 rounded-md bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {advancing ? "Avançando..." : "Marcar como concluída e continuar"}
              <ArrowRight size={14} />
            </button>
          )}
          {showNextCta && (
            <Link
              to="/membros/meu-projeto/missao/$id"
              params={{ id: next!.id }}
              search={{}}
              className="inline-flex items-center gap-2 rounded-md bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white"
            >
              Continuar para {next!.title} <ArrowRight size={14} />
            </Link>
          )}
          {finalized && journeyComplete && (
            <Link
              to="/membros/meu-negocio"
              className="inline-flex items-center gap-2 rounded-md bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white"
            >
              Ver meu negócio completo <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}


// helpers de decisão canonicalizados em @/lib/decision-engine.


